import type { NamespaceCatalog } from '@/i18n/ns';

type Keys =
  | 'microphoneLevel'
  | 'meetingAudioLevel'
  | 'silenceHint'
  | 'downloadsFallback'
  | 'desktopSource'
  | 'desktopSourceHint'
  | 'reminders'
  | 'remindersHint'
  | 'detected'
  | 'takeNotes'
  | 'dismiss'
  | 'disable'
  | 'unavailable'
  | 'nativeDenied'
  | 'nativeUnavailable';
type Copy = Record<Keys, string>;
const en: Copy = {
  microphoneLevel: 'Microphone',
  meetingAudioLevel: 'Meeting audio',
  silenceHint: 'No audio level yet. Check the selected input if someone is speaking.',
  downloadsFallback: 'This browser cannot capture meeting audio. Open Settings → Downloads.',
  desktopSource: 'Computer audio',
  desktopSourceHint: 'Audio from this computer is captured directly. No screen sharing is needed.',
  reminders: 'Meeting reminders',
  remindersHint:
    'Notice when another app uses the microphone. Audio is recorded only after you start.',
  detected: 'A call may be in progress',
  takeNotes: 'Take notes',
  dismiss: 'Not now',
  disable: 'Turn off reminders',
  unavailable: 'Meeting reminders are unavailable on this device',
  nativeDenied:
    'Allow computer audio when prompted, then try again. If access is still blocked, check your system audio permissions.',
  nativeUnavailable:
    'Computer audio could not start. Check your audio output and system audio permissions, then try again.',
};
const t = (
  microphoneLevel: string,
  meetingAudioLevel: string,
  silenceHint: string,
  downloadsFallback: string,
  desktopSource: string,
  desktopSourceHint: string,
  reminders: string,
  remindersHint: string,
  detected: string,
  takeNotes: string,
  dismiss: string,
  disable: string,
  unavailable: string,
  nativeDenied: string,
  nativeUnavailable: string,
): Copy => ({
  microphoneLevel,
  meetingAudioLevel,
  silenceHint,
  downloadsFallback,
  desktopSource,
  desktopSourceHint,
  reminders,
  remindersHint,
  detected,
  takeNotes,
  dismiss,
  disable,
  unavailable,
  nativeDenied,
  nativeUnavailable,
});
const translations: Record<string, Copy> = {
  bg: t(
    'Микрофон',
    'Аудио от срещата',
    'Все още няма ниво на звука. Проверете избрания вход, ако някой говори.',
    'Този браузър не може да записва аудио от срещата. Отворете Настройки → Изтегляния.',
    'Аудио от компютъра',
    'Аудиото от този компютър се записва директно. Не е нужно споделяне на екрана.',
    'Напомняния за срещи',
    'Уведомява, когато друго приложение използва микрофона. Аудио се записва само след стартиране.',
    'Възможно е да има разговор',
    'Водене на бележки',
    'Не сега',
    'Изключване на напомнянията',
    'Напомнянията за срещи не са налични на това устройство',
    'Разрешете компютърното аудио, когато бъдете подканени, и опитайте отново. Ако достъпът все още е блокиран, проверете системните разрешения за аудио.',
    'Компютърното аудио не може да стартира. Проверете аудио изхода и системните разрешения за аудио, след което опитайте отново.',
  ),
  hr: t(
    'Mikrofon',
    'Zvuk sastanka',
    'Još nema razine zvuka. Provjerite odabrani ulaz ako netko govori.',
    'Ovaj preglednik ne može snimati zvuk sastanka. Otvorite Postavke → Preuzimanja.',
    'Zvuk računala',
    'Zvuk s ovog računala snima se izravno. Dijeljenje zaslona nije potrebno.',
    'Podsjetnici za sastanak',
    'Obavijestite kada druga aplikacija koristi mikrofon. Zvuk se snima tek nakon pokretanja.',
    'Možda je poziv u tijeku',
    'Bilježi bilješke',
    'Ne sada',
    'Isključi podsjetnike',
    'Podsjetnici za sastanak nisu dostupni na ovom uređaju',
    'Dopustite zvuk računala kada se to zatraži i pokušajte ponovno. Ako je pristup i dalje blokiran, provjerite sistemske dozvole za zvuk.',
    'Zvuk računala nije moguće pokrenuti. Provjerite audio izlaz i sistemske dozvole za zvuk, pa pokušajte ponovno.',
  ),
  cs: t(
    'Mikrofon',
    'Zvuk schůzky',
    'Úroveň zvuku zatím není k dispozici. Pokud někdo mluví, zkontrolujte vybraný vstup.',
    'Tento prohlížeč nemůže zachytit zvuk schůzky. Otevřete Nastavení → Stažené soubory.',
    'Zvuk počítače',
    'Zvuk z tohoto počítače se zachytává přímo. Sdílení obrazovky není potřeba.',
    'Připomenutí schůzky',
    'Upozorní, když mikrofon používá jiná aplikace. Zvuk se nahrává až po spuštění.',
    'Možná probíhá hovor',
    'Psát poznámky',
    'Teď ne',
    'Vypnout připomenutí',
    'Připomenutí schůzky nejsou na tomto zařízení dostupná',
    'Po zobrazení výzvy povolte zvuk počítače a zkuste to znovu. Pokud je přístup stále blokován, zkontrolujte systémová oprávnění zvuku.',
    'Zvuk počítače se nepodařilo spustit. Zkontrolujte zvukový výstup a systémová oprávnění zvuku a zkuste to znovu.',
  ),
  da: t(
    'Mikrofon',
    'Mødelyd',
    'Intet lydniveau endnu. Kontrollér den valgte indgang, hvis nogen taler.',
    'Denne browser kan ikke optage mø­delyd. Åbn Indstillinger → Downloads.',
    'Computerlyd',
    'Lyden fra denne computer optages direkte. Skærmdeling er ikke nødvendig.',
    'Mødepåmindelser',
    'Giv besked, når en anden app bruger mikrofonen. Lyd optages først, når du starter.',
    'Et opkald kan være i gang',
    'Tag noter',
    'Ikke nu',
    'Slå påmindelser fra',
    'Mødepåmindelser er ikke tilgængelige på denne enhed',
    'Tillad computerlyd, når du bliver bedt om det, og prøv igen. Hvis adgangen stadig er blokeret, så kontrollér systemets lydtilladelser.',
    'Computerlyd kunne ikke startes. Kontrollér lydudgangen og systemets lydtilladelser, og prøv igen.',
  ),
  nl: t(
    'Microfoon',
    'Vergaderaudio',
    'Nog geen audioniveau. Controleer de gekozen ingang als iemand spreekt.',
    'Deze browser kan vergaderaudio niet vastleggen. Open Instellingen → Downloads.',
    'Computeraudio',
    'Audio van deze computer wordt rechtstreeks vastgelegd. Scherm delen is niet nodig.',
    'Vergaderherinneringen',
    'Meld wanneer een andere app de microfoon gebruikt. Audio wordt pas opgenomen nadat je start.',
    'Er is mogelijk een gesprek bezig',
    'Notities maken',
    'Niet nu',
    'Herinneringen uitschakelen',
    'Vergaderherinneringen zijn niet beschikbaar op dit apparaat',
    'Sta computeraudio toe wanneer daarom wordt gevraagd en probeer het opnieuw. Als de toegang geblokkeerd blijft, controleer dan de systeemrechten voor audio.',
    'Computeraudio kon niet worden gestart. Controleer de audio-uitvoer en de systeemrechten voor audio en probeer het opnieuw.',
  ),
  et: t(
    'Mikrofon',
    'Koosolekuheli',
    'Helitaset pole veel. Kui keegi räägib, kontrolli valitud sisendit.',
    'See brauser ei saa koosolekuheli salvestada. Ava Seaded → Allalaadimised.',
    'Arvutiheli',
    'Selle arvuti heli salvestatakse otse. Ekraani jagamist pole vaja.',
    'Koosoleku meeldetuletused',
    'Teavita, kui mõni teine rakendus kasutab mikrofoni. Heli salvestatakse alles pärast alustamist.',
    'Kõne võib olla käimas',
    'Tee märkmeid',
    'Mitte praegu',
    'Lülita meeldetuletused välja',
    'Koosoleku meeldetuletused pole selles seadmes saadaval',
    'Luba arvutiheli, kui seda küsitakse, ja proovi uuesti. Kui juurdepääs on endiselt blokeeritud, kontrolli süsteemi heliõigusi.',
    'Arvutiheli ei saanud käivitada. Kontrolli heliväljundit ja süsteemi heliõigusi ning proovi uuesti.',
  ),
  fi: t(
    'Mikrofoni',
    'Kokouksen ääni',
    'Äänitasoa ei vielä ole. Tarkista valittu tulo, jos joku puhuu.',
    'Tämä selain ei voi kaapata kokouksen ääntä. Avaa Asetukset → Lataukset.',
    'Tietokoneen ääni',
    'Tämän tietokoneen ääni tallennetaan suoraan. Näytön jakamista ei tarvita.',
    'Kokousmuistutukset',
    'Ilmoita, kun toinen sovellus käyttää mikrofonia. Ääntä tallennetaan vasta aloittamisen jälkeen.',
    'Puhelu saattaa olla käynnissä',
    'Tee muistiinpanoja',
    'Ei nyt',
    'Poista muistutukset käytöstä',
    'Kokousmuistutukset eivät ole käytettävissä tällä laitteella',
    'Salli tietokoneen ääni pyydettäessä ja yritä uudelleen. Jos käyttö on edelleen estetty, tarkista järjestelmän ääni käyttöoikeudet.',
    'Tietokoneen ääntä ei voitu käynnistää. Tarkista äänilähtö ja järjestelmän ääni käyttöoikeudet ja yritä uudelleen.',
  ),
  fr: t(
    'Microphone',
    'Audio de la réunion',
    'Aucun niveau audio pour le moment. Vérifiez l’entrée sélectionnée si quelqu’un parle.',
    'Ce navigateur ne peut pas capturer l’audio de la réunion. Ouvrez Paramètres → Téléchargements.',
    'Audio de l’ordinateur',
    'L’audio de cet ordinateur est capturé directement. Le partage d’écran est inutile.',
    'Rappels de réunion',
    'Signalez quand une autre application utilise le micro. L’audio est enregistré seulement après le démarrage.',
    'Un appel est peut-être en cours',
    'Prendre des notes',
    'Pas maintenant',
    'Désactiver les rappels',
    'Les rappels de réunion ne sont pas disponibles sur cet appareil',
    'Autorisez l’audio de l’ordinateur lorsque vous y êtes invité, puis réessayez. Si l’accès reste bloqué, vérifiez les autorisations audio du système.',
    'L’audio de l’ordinateur n’a pas pu démarrer. Vérifiez la sortie audio et les autorisations audio du système, puis réessayez.',
  ),
  de: t(
    'Mikrofon',
    'Meeting-Audio',
    'Noch kein Audiopegel. Prüfen Sie den ausgewählten Eingang, wenn jemand spricht.',
    'Dieser Browser kann Meeting-Audio nicht aufnehmen. Öffnen Sie Einstellungen → Downloads.',
    'Computer-Audio',
    'Audio von diesem Computer wird direkt aufgenommen. Bildschirmfreigabe ist nicht nötig.',
    'Meeting-Erinnerungen',
    'Melde, wenn eine andere App das Mikrofon verwendet. Audio wird erst nach dem Start aufgenommen.',
    'Möglicherweise läuft ein Anruf',
    'Notizen machen',
    'Nicht jetzt',
    'Erinnerungen ausschalten',
    'Meeting-Erinnerungen sind auf diesem Gerät nicht verfügbar',
    'Erlauben Sie den Computerton, wenn Sie dazu aufgefordert werden, und versuchen Sie es erneut. Wenn der Zugriff weiterhin blockiert ist, überprüfen Sie die Audioberechtigungen Ihres Systems.',
    'Der Computerton konnte nicht gestartet werden. Überprüfen Sie Ihren Audioausgang und die Audioberechtigungen Ihres Systems und versuchen Sie es erneut.',
  ),
  el: t(
    'Μικρόφωνο',
    'Ήχος σύσκεψης',
    'Δεν υπάρχει ακόμη επίπεδο ήχου. Ελέγξτε την επιλεγμένη είσοδο αν κάποιος μιλά.',
    'Αυτό το πρόγραμμα περιήγησης δεν μπορεί να καταγράψει τον ήχο της σύσκεψης. Ανοίξτε Ρυθμίσεις → Λήψεις.',
    'Ήχος υπολογιστή',
    'Ο ήχος αυτού του υπολογιστή καταγράφεται απευθείας. Δεν χρειάζεται κοινή χρήση οθόνης.',
    'Υπενθυμίσεις σύσκεψης',
    'Ειδοποίηση όταν άλλη εφαρμογή χρησιμοποιεί το μικρόφωνο. Η εγγραφή ξεκινά μόνο αφού ξεκινήσετε.',
    'Μπορεί να βρίσκεται σε εξέλιξη κλήση',
    'Λήψη σημειώσεων',
    'Όχι τώρα',
    'Απενεργοποίηση υπενθυμίσεων',
    'Οι υπενθυμίσεις σύσκεψης δεν είναι διαθέσιμες σε αυτήν τη συσκευή',
    'Επιτρέψτε τον ήχο του υπολογιστή όταν σας ζητηθεί και δοκιμάστε ξανά. Αν η πρόσβαση εξακολουθεί να είναι αποκλεισμένη, ελέγξτε τα δικαιώματα ήχου του συστήματος.',
    'Δεν ήταν δυνατή η έναρξη του ήχου του υπολογιστή. Ελέγξτε την έξοδο ήχου και τα δικαιώματα ήχου του συστήματος και δοκιμάστε ξανά.',
  ),
  hu: t(
    'Mikrofon',
    'Értekezlet hangja',
    'Még nincs hangszint. Ellenőrizd a kiválasztott bemenetet, ha valaki beszél.',
    'Ez a böngésző nem tudja rögzíteni az értekezlet hangját. Nyisd meg: Beállítások → Letöltések.',
    'Számítógép hangja',
    'A számítógép hangját közvetlenül rögzítjük. Képernyőmegosztás nem szükséges.',
    'Értekezleti emlékeztetők',
    'Jelezze, ha egy másik alkalmazás használja a mikrofont. A hang csak indítás után rögzül.',
    'Lehet, hogy hívás van folyamatban',
    'Jegyzetelés',
    'Most nem',
    'Emlékeztetők kikapcsolása',
    'Az értekezleti emlékeztetők nem érhetők el ezen az eszközön',
    'Engedélyezd a számítógép hangját, amikor a rendszer kéri, majd próbáld újra. Ha a hozzáférés továbbra is blokkolva van, ellenőrizd a rendszer hangengedélyeit.',
    'A számítógép hangját nem sikerült elindítani. Ellenőrizd a hangkimenetet és a rendszer hangengedélyeit, majd próbáld újra.',
  ),
  ga: t(
    'Micreafón',
    'Fuaim an chruinnithe',
    'Níl leibhéal fuaime ann fós. Seiceáil an t-ionchur roghnaithe má tá duine ag labhairt.',
    'Ní féidir leis an mbrabhsálaí seo fuaim an chruinnithe a ghabháil. Oscail Socruithe → Íoslódálacha.',
    'Fuaim an ríomhaire',
    'Gabhann muid fuaim an ríomhaire seo go díreach. Níl comhroinnt scáileáin riachtanach.',
    'Meabhrúcháin cruinnithe',
    'Tabhair fógra nuair a úsáideann aip eile an micreafón. Ní thaifeadtar fuaim go dtí go dtosaíonn tú.',
    'Seans go bhfuil glao ar siúl',
    'Glac nótaí',
    'Ní anois',
    'Múch meabhrúcháin',
    'Níl meabhrúcháin cruinnithe ar fáil ar an ngléas seo',
    'Ceadaigh fuaim an ríomhaire nuair a iarrtar ort agus bain triail eile as. Má tá an rochtain blocáilte fós, seiceáil ceadanna fuaime an chórais.',
    'Níorbh fhéidir fuaim an ríomhaire a thosú. Seiceáil aschur fuaime agus ceadanna fuaime an chórais, ansin bain triail eile as.',
  ),
  it: t(
    'Microfono',
    'Audio della riunione',
    'Nessun livello audio ancora. Controlla l’ingresso selezionato se qualcuno sta parlando.',
    'Questo browser non può acquisire l’audio della riunione. Apri Impostazioni → Download.',
    'Audio del computer',
    'L’audio di questo computer viene acquisito direttamente. Non serve condividere lo schermo.',
    'Promemoria riunione',
    'Avvisa quando un’altra app usa il microfono. L’audio viene registrato solo dopo l’avvio.',
    'Potrebbe essere in corso una chiamata',
    'Prendi appunti',
    'Non ora',
    'Disattiva promemoria',
    'I promemoria riunione non sono disponibili su questo dispositivo',
    'Consenti l’audio del computer quando richiesto e riprova. Se l’accesso è ancora bloccato, controlla i permessi audio del sistema.',
    'Non è stato possibile avviare l’audio del computer. Controlla l’uscita audio e i permessi audio del sistema, quindi riprova.',
  ),
  lv: t(
    'Mikrofons',
    'Sapulces audio',
    'Audio līmenis vēl nav redzams. Ja kāds runā, pārbaudiet izvēlēto ievadi.',
    'Šī pārlūkprogramma nevar uztvert sapulces audio. Atveriet Iestatījumi → Lejupielādes.',
    'Datora audio',
    'Šī datora audio tiek uztverts tieši. Ekrāna kopīgošana nav vajadzīga.',
    'Sapulču atgādinājumi',
    'Paziņot, kad cita lietotne izmanto mikrofonu. Audio tiek ierakstīts tikai pēc palaišanas.',
    'Iespējams, notiek zvans',
    'Veikt piezīmes',
    'Ne tagad',
    'Izslēgt atgādinājumus',
    'Sapulču atgādinājumi šajā ierīcē nav pieejami',
    'Kad tiek prasīts, atļaujiet datora audio un mēģiniet vēlreiz. Ja piekļuve joprojām ir bloķēta, pārbaudiet sistēmas audio atļaujas.',
    'Datora audio nevarēja palaist. Pārbaudiet audio izvadi un sistēmas audio atļaujas, pēc tam mēģiniet vēlreiz.',
  ),
  lt: t(
    'Mikrofonas',
    'Susitikimo garsas',
    'Garso lygio dar nėra. Jei kas nors kalba, patikrinkite pasirinktą įvestį.',
    'Ši naršyklė negali užfiksuoti susitikimo garso. Atidarykite Nustatymai → Atsisiuntimai.',
    'Kompiuterio garsas',
    'Šio kompiuterio garsas fiksuojamas tiesiogiai. Ekrano bendrinti nereikia.',
    'Susitikimų priminimai',
    'Pranešti, kai kita programa naudoja mikrofoną. Garsas įrašomas tik pradėjus.',
    'Gali vykti skambutis',
    'Rašyti pastabas',
    'Ne dabar',
    'Išjungti priminimus',
    'Susitikimų priminimai šiame įrenginyje nepasiekiami',
    'Kai būsite paraginti, leiskite kompiuterio garsą ir bandykite dar kartą. Jei prieiga vis dar blokuojama, patikrinkite sistemos garso leidimus.',
    'Nepavyko paleisti kompiuterio garso. Patikrinkite garso išvestį ir sistemos garso leidimus, tada bandykite dar kartą.',
  ),
  mt: t(
    'Mikrofonu',
    'Awdjo tal-laqgħa',
    'Għad m’hemmx livell tal-awdjo. Iċċekkja l-input magħżul jekk xi ħadd qed jitkellem.',
    'Dan il-browser ma jistax jaqbad l-awdjo tal-laqgħa. Iftaħ Settings → Downloads.',
    'Awdjo tal-kompjuter',
    'L-awdjo minn dan il-kompjuter jinqabad direttament. M’hemmx bżonn taqsam l-iskrin.',
    'Tifkiriet tal-laqgħat',
    'Avża meta app oħra tkun qed tuża l-mikrofonu. L-awdjo jiġi rrekordjat biss wara li tibda.',
    'Jista’ jkun għaddej telefonata',
    'Ħu noti',
    'Mhux issa',
    'Itfi t-tifkiriet',
    'It-tifkiriet tal-laqgħat mhumiex disponibbli fuq dan l-apparat',
    'Ħalli l-awdjo tal-kompjuter meta tintalab u erġa’ pprova. Jekk l-aċċess għadu mblukkat, iċċekkja l-permessi tal-awdjo tas-sistema.',
    'L-awdjo tal-kompjuter ma setax jibda. Iċċekkja l-output tal-awdjo u l-permessi tal-awdjo tas-sistema, imbagħad erġa’ pprova.',
  ),
  pl: t(
    'Mikrofon',
    'Dźwięk spotkania',
    'Brak poziomu dźwięku. Jeśli ktoś mówi, sprawdź wybrane wejście.',
    'Ta przeglądarka nie może przechwycić dźwięku spotkania. Otwórz Ustawienia → Pobrane.',
    'Dźwięk komputera',
    'Dźwięk z tego komputera jest przechwytywany bezpośrednio. Udostępnianie ekranu nie jest potrzebne.',
    'Przypomnienia o spotkaniu',
    'Powiadom, gdy inna aplikacja używa mikrofonu. Dźwięk jest nagrywany dopiero po rozpoczęciu.',
    'Rozmowa może być w toku',
    'Rób notatki',
    'Nie teraz',
    'Wyłącz przypomnienia',
    'Przypomnienia o spotkaniu są niedostępne na tym urządzeniu',
    'Zezwól na dźwięk komputera po wyświetleniu prośby i spróbuj ponownie. Jeśli dostęp nadal jest zablokowany, sprawdź systemowe uprawnienia audio.',
    'Nie udało się uruchomić dźwięku komputera. Sprawdź wyjście audio i systemowe uprawnienia audio, a następnie spróbuj ponownie.',
  ),
  pt: t(
    'Microfone',
    'Áudio da reunião',
    'Ainda não há nível de áudio. Verifique a entrada selecionada se alguém estiver a falar.',
    'Este navegador não consegue captar o áudio da reunião. Abra Definições → Transferências.',
    'Áudio do computador',
    'O áudio deste computador é captado diretamente. Não é necessário partilhar o ecrã.',
    'Lembretes de reuniões',
    'Avise quando outra aplicação usar o microfone. O áudio só é gravado depois de iniciar.',
    'Pode estar a decorrer uma chamada',
    'Tirar notas',
    'Agora não',
    'Desativar lembretes',
    'Os lembretes de reuniões não estão disponíveis neste dispositivo',
    'Permita o áudio do computador quando solicitado e tente novamente. Se o acesso continuar bloqueado, verifique as permissões de áudio do sistema.',
    'Não foi possível iniciar o áudio do computador. Verifique a saída de áudio e as permissões de áudio do sistema e tente novamente.',
  ),
  ro: t(
    'Microfon',
    'Sunetul întâlnirii',
    'Încă nu există nivel audio. Verifică intrarea selectată dacă cineva vorbește.',
    'Acest browser nu poate captura sunetul întâlnirii. Deschide Setări → Descărcări.',
    'Sunetul computerului',
    'Sunetul acestui computer este capturat direct. Nu este necesară partajarea ecranului.',
    'Mementouri pentru întâlniri',
    'Anunță când o altă aplicație folosește microfonul. Sunetul este înregistrat doar după pornire.',
    'Este posibil să fie un apel în desfășurare',
    'Ia notițe',
    'Nu acum',
    'Dezactivează mementourile',
    'Mementourile pentru întâlniri nu sunt disponibile pe acest dispozitiv',
    'Permite sunetul computerului când ți se solicită și încearcă din nou. Dacă accesul este în continuare blocat, verifică permisiunile audio ale sistemului.',
    'Sunetul computerului nu a putut porni. Verifică ieșirea audio și permisiunile audio ale sistemului, apoi încearcă din nou.',
  ),
  sk: t(
    'Mikrofón',
    'Zvuk schôdze',
    'Úroveň zvuku zatiaľ nie je k dispozícii. Ak niekto hovorí, skontrolujte vybraný vstup.',
    'Tento prehliadač nedokáže zachytiť zvuk schôdze. Otvorte Nastavenia → Stiahnuté súbory.',
    'Zvuk počítača',
    'Zvuk z tohto počítača sa zachytáva priamo. Zdieľanie obrazovky nie je potrebné.',
    'Pripomenutia schôdze',
    'Upozornite, keď mikrofón používa iná aplikácia. Zvuk sa nahráva až po spustení.',
    'Možno prebieha hovor',
    'Robiť poznámky',
    'Teraz nie',
    'Vypnúť pripomenutia',
    'Pripomenutia schôdze nie sú na tomto zariadení dostupné',
    'Po zobrazení výzvy povoľte zvuk počítača a skúste to znova. Ak je prístup stále zablokovaný, skontrolujte systémové povolenia zvuku.',
    'Zvuk počítača sa nepodarilo spustiť. Skontrolujte zvukový výstup a systémové povolenia zvuku a skúste to znova.',
  ),
  sl: t(
    'Mikrofon',
    'Zvok sestanka',
    'Raven zvoka še ni. Če nekdo govori, preverite izbrani vhod.',
    'Ta brskalnik ne more zajeti zvoka sestanka. Odprite Nastavitve → Prenosi.',
    'Zvok računalnika',
    'Zvok tega računalnika se zajema neposredno. Skupna raba zaslona ni potrebna.',
    'Opomniki za sestanke',
    'Obvesti, ko druga aplikacija uporablja mikrofon. Zvok se snema šele po začetku.',
    'Morda poteka klic',
    'Zapiši opombe',
    'Ne zdaj',
    'Izklopi opomnike',
    'Opomniki za sestanke v tej napravi niso na voljo',
    'Ob pozivu dovolite zvok računalnika in poskusite znova. Če je dostop še vedno blokiran, preverite sistemska dovoljenja za zvok.',
    'Zvoka računalnika ni bilo mogoče zagnati. Preverite zvočni izhod in sistemska dovoljenja za zvok ter poskusite znova.',
  ),
  es: t(
    'Micrófono',
    'Audio de la reunión',
    'Aún no hay nivel de audio. Comprueba la entrada seleccionada si alguien está hablando.',
    'Este navegador no puede capturar el audio de la reunión. Abre Ajustes → Descargas.',
    'Audio del ordenador',
    'El audio de este ordenador se captura directamente. No hace falta compartir la pantalla.',
    'Recordatorios de reuniones',
    'Avisar cuando otra aplicación use el micrófono. El audio solo se graba después de iniciar.',
    'Puede haber una llamada en curso',
    'Tomar notas',
    'Ahora no',
    'Desactivar recordatorios',
    'Los recordatorios de reuniones no están disponibles en este dispositivo',
    'Permite el audio del ordenador cuando se le solicite y vuelva a intentarlo. Si el acceso sigue bloqueado, compruebe los permisos de audio del sistema.',
    'No se pudo iniciar el audio del ordenador. Compruebe la salida de audio y los permisos de audio del sistema y vuelva a intentarlo.',
  ),
  sv: t(
    'Mikrofon',
    'Mötesljud',
    'Ingen ljudnivå ännu. Kontrollera vald ingång om någon pratar.',
    'Den här webbläsaren kan inte fånga mötesljud. Öppna Inställningar → Nedladdningar.',
    'Datorljud',
    'Ljud från den här datorn fångas direkt. Ingen skärmdelning behövs.',
    'Mötespåminnelser',
    'Visa när en annan app använder mikrofonen. Ljud spelas bara in efter att du startat.',
    'Ett samtal kan pågå',
    'Anteckna',
    'Inte nu',
    'Stäng av påminnelser',
    'Mötespåminnelser är inte tillgängliga på den här enheten',
    'Tillåt datorljud när du uppmanas till det och försök igen. Om åtkomsten fortfarande är blockerad kontrollerar du systemets ljudbehörigheter.',
    'Datorljudet kunde inte startas. Kontrollera ljudutgången och systemets ljudbehörigheter och försök igen.',
  ),
};
export default { en, ...translations } as NamespaceCatalog<Keys>;
